/**
 * ローカルストレージ関連のサービス
 * OPFS (Origin Private File System) とIndexedDBを使用したデータ永続化
 */

import { MIDASProject, createTransientAwareReplacer } from '../../types';
import logger from '../utils/logger';
import { migrateToLatest } from './migrations';
import { compressGzip, decompressGzip } from './compressionService';

/**
 * OPFSのルートディレクトリを取得
 */
async function getOPFSRoot(): Promise<FileSystemDirectoryHandle> {
  if (!navigator.storage || !navigator.storage.getDirectory) {
    throw new Error('OPFS is not supported in this browser');
  }
  return await navigator.storage.getDirectory();
}

/**
 * gzip圧縮されたMDSファイルのバイナリデータをデシリアライズ
 * @param data gzip圧縮されたバイナリデータ
 * @returns デシリアライズされたプロジェクトデータ（マイグレーション適用後）
 */
export async function deserializeMDSFile(data: Uint8Array): Promise<MIDASProject> {
  // gzip形式であることを確認（マジックバイト: 0x1f, 0x8b）
  if (data.length < 2 || data[0] !== 0x1f || data[1] !== 0x8b) {
    throw new Error('Invalid MDS file format: file must be gzip-compressed');
  }

  // gzip展開
  const jsonString = await decompressGzip(data);

  // JSONパース
  const rawData = JSON.parse(jsonString);

  // マイグレーション適用
  return migrateToLatest(rawData);
}

/**
 * プロジェクトをOPFSに保存（Web Locks APIで排他制御）
 */
export async function saveProject(
  project: MIDASProject,
  filename?: string
): Promise<void> {
  const fileName = filename || `${project.id}.mds`;
  const lockName = `midas-project-${fileName}`;

  // Web Locks APIが利用可能か確認
  if ('locks' in navigator) {
    // Web Locks APIで排他制御
    await navigator.locks.request(lockName, async () => {
      await saveProjectInternal(project, fileName);
    });
  } else {
    // Web Locks APIが使えない場合は警告を出して通常保存
    logger.warn('[storageService] Web Locks API not supported - concurrent access may cause issues');
    await saveProjectInternal(project, fileName);
  }
}

/**
 * 実際のプロジェクト保存処理（内部用）
 */
async function saveProjectInternal(
  project: MIDASProject,
  fileName: string
): Promise<void> {
  let writable: FileSystemWritableFileStream | null = null;

  try {
    const startTime = performance.now();
    const root = await getOPFSRoot();
    const projectDir = await root.getDirectoryHandle('projects', { create: true });
    const fileHandle = await projectDir.getFileHandle(fileName, { create: true });

    writable = await fileHandle.createWritable();

    // プロジェクト保存のログ（IO操作なので残す）
    logger.info('[storageService] Saving project to OPFS:', {
      fileName,
      datasets: Object.keys(project.datasets || {}).length,
      reports: project.reports?.length || 0,
      workspaces: project.workspaces?.length || 0
    });

    // transientプロパティを除外してシリアライズ（minified）
    // DerivedDataSetの__transient__マーカーにより、dataプロパティが自動的に除外される
    const jsonString = JSON.stringify(project, createTransientAwareReplacer());
    const originalSize = jsonString.length;

    // gzip圧縮
    const compressed = await compressGzip(jsonString);
    const compressedSize = compressed.length;

    // OPFS保存
    await writable.write(compressed);
    await writable.close();
    writable = null;

    // メタデータをIndexedDBに保存
    await saveProjectMetadata(
      project.id,
      fileName,
      project.projectName,
      new Date(),
      project.sourceUrl,
      project.sourceUrlFetchedAt
    );

    // ログ出力
    const duration = performance.now() - startTime;
    const ratio = (1 - compressedSize / originalSize) * 100;

    logger.info('[storageService] Project saved successfully', {
      fileName,
      originalSize,
      compressedSize,
      compressionRatio: `${ratio.toFixed(1)}%`,
      duration: `${duration.toFixed(0)}ms`
    });
  } catch (error) {

    // storage quota超過の場合は詳細情報を追加
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      logger.error('[storageService] QUOTA EXCEEDED: Storage quota limit reached');

      try {
        const estimate = await navigator.storage.estimate();
        const usageInfo = {
          usedMB: ((estimate.usage || 0) / 1024 / 1024).toFixed(2),
          quotaMB: ((estimate.quota || 0) / 1024 / 1024).toFixed(2),
          usagePercent: (((estimate.usage || 0) / (estimate.quota || 1)) * 100).toFixed(2)
        };
        logger.error('[storageService] Storage usage:', usageInfo);
      } catch (estimateError) {
        logger.error('[storageService] Could not get storage estimate:', estimateError);
      }
    }

    logger.error('[storageService] Failed to save project:', error);
    throw error;
  }
}

/**
 * OPFSからプロジェクトを読み込み（Web Locks APIで読み込み保護）
 */
export async function loadProject(filename: string): Promise<MIDASProject | null> {
  const lockName = `midas-project-${filename}`;

  // Web Locks APIが利用可能か確認
  if ('locks' in navigator) {
    // 共有ロック（読み込みは同時実行可能）
    return await navigator.locks.request(
      lockName,
      { mode: 'shared' },
      async () => {
        return await loadProjectInternal(filename);
      }
    );
  } else {
    logger.warn('[storageService] Web Locks API not supported - concurrent access may cause issues during load');
    return await loadProjectInternal(filename);
  }
}

/**
 * 実際のプロジェクト読み込み処理（内部用）
 */
async function loadProjectInternal(filename: string): Promise<MIDASProject | null> {
  try {
    const startTime = performance.now();
    const root = await getOPFSRoot();
    const projectDir = await root.getDirectoryHandle('projects', { create: false });
    const fileHandle = await projectDir.getFileHandle(filename);
    const file = await fileHandle.getFile();

    // ArrayBufferとして読み込み
    const arrayBuffer = await file.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);

    // デシリアライズ（gzip展開、JSONパース、マイグレーション適用）
    const project = await deserializeMDSFile(data);
    const jsonString = JSON.stringify(project); // ログ用にサイズ計算

    const duration = performance.now() - startTime;
    logger.info('[storageService] Project loaded from OPFS', {
      filename,
      compressedSize: data.length,
      decompressedSize: jsonString.length,
      datasets: Object.keys(project.datasets || {}).length,
      reports: project.reports?.length || 0,
      version: project.version,
      duration: `${duration.toFixed(0)}ms`
    });

    return project;
  } catch (error) {
    if (error instanceof Error && error.name === 'NotFoundError') {
      return null;
    }
    logger.error('[storageService] Failed to load project:', error);
    throw error;
  }
}

/**
 * プロジェクトが他のタブでロックされているか確認
 */
export async function isProjectLocked(projectId: string): Promise<boolean> {
  // Web Locks APIが利用可能か確認
  if (!('locks' in navigator)) {
    return false; // APIが使えない場合はロックされていないとみなす
  }

  const fileName = `${projectId}.mds`;
  const lockName = `midas-project-${fileName}`;
  let isLocked = false;

  // ifAvailable: true で即座に結果を返す
  await navigator.locks.request(
    lockName,
    { ifAvailable: true },
    async (lock) => {
      if (!lock) {
        // ロックが取得できない = 他で使用中
        isLocked = true;
      }
      // ロックが取得できた場合は即座に解放される
    }
  );

  if (isLocked) {
    logger.info('[storageService] Project is locked by another tab:', projectId);
  }

  return isLocked;
}

/**
 * 現在のロック状態を取得（デバッグ用）
 */
export async function getLocksStatus(): Promise<{
  supported: boolean;
  held?: unknown[];
  pending?: unknown[];
}> {
  if (!('locks' in navigator)) {
    return { supported: false };
  }

  const state = await navigator.locks.query();
  return {
    supported: true,
    held: state.held,
    pending: state.pending
  };
}

/**
 * プロジェクト一覧を取得
 */
export interface ProjectListItem {
  filename: string;
  size: number;
  metadata?: {
    projectName?: string;
    lastModified?: string;
    description?: string;
    projectId?: string;
  };
}

export async function listProjects(): Promise<ProjectListItem[]> {
  try {
    const root = await getOPFSRoot();
    const projectDir = await root.getDirectoryHandle('projects', { create: true });

    const projects: ProjectListItem[] = [];

    for await (const entry of projectDir.values()) {
      if (entry.kind === 'file' && entry.name.endsWith('.mds')) {
        // IndexedDBからメタデータを取得（ファイルを読み込まずにメタデータのみ使用）
        const metadata = await getProjectMetadata(entry.name);

        // ファイルサイズを取得
        const fileHandle = await projectDir.getFileHandle(entry.name);
        const file = await fileHandle.getFile();

        projects.push({
          filename: entry.name,
          size: file.size,
          metadata: {
            ...metadata,
            projectName: metadata?.projectName || entry.name.replace('.mds', '')
          }
        });
      }
    }

    // 最終更新日でソート（新しい順）
    projects.sort((a, b) => {
      const dateA = a.metadata?.lastModified ? new Date(a.metadata.lastModified).getTime() : 0;
      const dateB = b.metadata?.lastModified ? new Date(b.metadata.lastModified).getTime() : 0;
      return dateB - dateA;
    });

    return projects;
  } catch (error) {
    logger.error('[storageService] Failed to list projects:', error);
    return [];
  }
}

/**
 * プロジェクトを削除（Web Locks APIで排他制御）
 */
export async function deleteProject(filename: string): Promise<void> {
  const lockName = `midas-project-${filename}`;

  // Web Locks APIが利用可能か確認
  if ('locks' in navigator) {
    // 排他ロック（削除は排他的に行う）
    await navigator.locks.request(lockName, async () => {
      await deleteProjectInternal(filename);
    });
  } else {
    logger.warn('[storageService] Web Locks API not supported - concurrent access may cause issues during delete');
    await deleteProjectInternal(filename);
  }
}

/**
 * 実際のプロジェクト削除処理（内部用）
 */
async function deleteProjectInternal(filename: string): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const projectDir = await root.getDirectoryHandle('projects', { create: false });
    await projectDir.removeEntry(filename);

    // メタデータも削除
    await deleteProjectMetadata(filename);

    logger.info('[storageService] Project deleted:', filename);
  } catch (error) {
    logger.error('[storageService] Failed to delete project:', error);
    throw error;
  }
}

/**
 * キャッシュデータをOPFSに保存
 */
export async function saveCacheData(
  key: string,
  data: unknown,
  category: string = 'cache'
): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const cacheDir = await root.getDirectoryHandle(category, { create: true });
    
    const fileHandle = await cacheDir.getFileHandle(`${key}.json`, { create: true });
    const writable = await fileHandle.createWritable();
    
    const jsonData = JSON.stringify(data);
    await writable.write(jsonData);
    await writable.close();
  } catch (error) {
    logger.error('[storageService] Failed to save cache data:', error);
    throw error;
  }
}

/**
 * キャッシュデータを読み込み
 */
export async function loadCacheData<T>(
  key: string,
  category: string = 'cache'
): Promise<T | null> {
  try {
    const root = await getOPFSRoot();
    const cacheDir = await root.getDirectoryHandle(category, { create: false });
    const fileHandle = await cacheDir.getFileHandle(`${key}.json`);
    const file = await fileHandle.getFile();
    const content = await file.text();
    
    return JSON.parse(content) as T;
  } catch {
    // ファイルが存在しない場合はnullを返す
    return null;
  }
}

// ===== IndexedDB Helper Functions =====

const DB_NAME = 'MIDASDatabase';
const DB_VERSION = 1;
const PROJECT_STORE = 'projectMetadata';
const RECENT_FILES_STORE = 'recentFiles';

/**
 * IndexedDBを開く
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains(PROJECT_STORE)) {
        db.createObjectStore(PROJECT_STORE, { keyPath: 'filename' });
      }
      
      if (!db.objectStoreNames.contains(RECENT_FILES_STORE)) {
        const store = db.createObjectStore(RECENT_FILES_STORE, { keyPath: 'path' });
        store.createIndex('lastOpened', 'lastOpened', { unique: false });
      }
    };
  });
}

/**
 * プロジェクトのメタデータを保存
 */
async function saveProjectMetadata(
  projectId: string,
  filename: string,
  projectName: string,
  lastModified: Date,
  sourceUrl?: string,
  sourceUrlFetchedAt?: string
): Promise<void> {
  const db = await openDB();
  const transaction = db.transaction([PROJECT_STORE], 'readwrite');
  const store = transaction.objectStore(PROJECT_STORE);

  await store.put({
    filename,
    projectId,
    projectName,
    lastModified: lastModified.toISOString(),
    sourceUrl,
    sourceUrlFetchedAt,
  });
}

/**
 * プロジェクトのメタデータを取得
 */
async function getProjectMetadata(filename: string): Promise<{
  filename: string;
  projectId?: string;
  projectName?: string;
  lastModified?: string;
  sourceUrl?: string;
  sourceUrlFetchedAt?: string;
} | undefined> {
  const db = await openDB();
  const transaction = db.transaction([PROJECT_STORE], 'readonly');
  const store = transaction.objectStore(PROJECT_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get(filename);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * プロジェクトのメタデータを削除
 */
async function deleteProjectMetadata(filename: string): Promise<void> {
  const db = await openDB();
  const transaction = db.transaction([PROJECT_STORE], 'readwrite');
  const store = transaction.objectStore(PROJECT_STORE);

  await store.delete(filename);
}

/**
 * sourceUrlで既存プロジェクトを検索
 */
export async function findProjectBySourceUrl(sourceUrl: string): Promise<{
  filename: string;
  projectId?: string;
  projectName?: string;
  lastModified?: string;
  sourceUrl?: string;
  sourceUrlFetchedAt?: string;
} | null> {
  const db = await openDB();
  const transaction = db.transaction([PROJECT_STORE], 'readonly');
  const store = transaction.objectStore(PROJECT_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const allMetadata = request.result;
      const found = allMetadata.find((meta: { sourceUrl?: string }) => meta.sourceUrl === sourceUrl);
      resolve(found || null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * 最近使用したファイルを記録
 */
export async function addRecentFile(
  path: string,
  name: string,
  type: 'csv' | 'mds'
): Promise<void> {
  const db = await openDB();
  const transaction = db.transaction([RECENT_FILES_STORE], 'readwrite');
  const store = transaction.objectStore(RECENT_FILES_STORE);
  
  await store.put({
    path,
    name,
    type,
    lastOpened: new Date().toISOString(),
  });
}

/**
 * 最近使用したファイル一覧を取得
 */
interface RecentFile {
  path: string;
  name: string;
  type: 'csv' | 'mds';
  lastOpened: string;
}

export async function getRecentFiles(limit: number = 10): Promise<RecentFile[]> {
  const db = await openDB();
  const transaction = db.transaction([RECENT_FILES_STORE], 'readonly');
  const store = transaction.objectStore(RECENT_FILES_STORE);
  const index = store.index('lastOpened');
  
  return new Promise((resolve, reject) => {
    const request = index.openCursor(null, 'prev');
    const results: RecentFile[] = [];
    
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor && results.length < limit) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    
    request.onerror = () => reject(request.error);
  });
}